import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Cloud, CheckCircle2, AlertCircle, Loader, ExternalLink, RefreshCw, Unplug, Folder } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function DriveFallbackPanel({ onSyncDone }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [files, setFiles] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/oauth/drive/status`);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    // detect oauth callback redirect
    if (new URLSearchParams(window.location.search).get('drive_connected') === '1') {
      toast.success('Google Drive conectado correctamente');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connect = async () => {
    try {
      const res = await axios.get(`${API}/oauth/drive/connect`);
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error iniciando OAuth');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar Google Drive?')) return;
    try {
      await axios.delete(`${API}/oauth/drive/disconnect`);
      toast.success('Drive desconectado');
      refresh();
    } catch {
      toast.error('Error');
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sync/trigger-drive`);
      const r = res.data?.result || {};
      toast.success(`Drive sync: +${r.created || 0} nuevos, ${r.updated || 0} actualizados, ${r.skipped || 0} ignorados`);
      onSyncDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sync Drive');
    } finally { setSyncing(false); }
  };

  const listFiles = async () => {
    try {
      const res = await axios.get(`${API}/sync/drive-files`);
      setFiles(res.data.files);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-border bg-card p-4" data-testid="drive-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
            status?.connected ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
          }`}>
            <Cloud className={`h-5 w-5 ${status?.connected ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Fallback Google Drive</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Si el SFTP falla, el sync diario cae automáticamente a este Drive. También puedes lanzarlo manualmente.
            </p>
            <div className="mt-2 text-xs">
              {loading ? (
                <span className="text-muted-foreground">Cargando…</span>
              ) : status?.connected ? (
                <span className="text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
                  {status.expiry && <span className="text-muted-foreground ml-2">expira: {new Date(status.expiry).toLocaleString('es-ES')}</span>}
                </span>
              ) : (
                <span className="text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" /> No conectado
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!status?.connected ? (
            <Button onClick={connect} size="sm" data-testid="btn-drive-connect">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Conectar Drive
            </Button>
          ) : (
            <>
              <Button onClick={listFiles} size="sm" variant="outline" data-testid="btn-drive-files">
                <Folder className="h-3.5 w-3.5 mr-1.5" /> Ver archivos
              </Button>
              <Button onClick={async () => {
                try {
                  const res = await axios.get(`${API}/oauth/drive/diagnostic`);
                  const d = res.data;
                  if (d.error) { toast.error(`Error: ${d.error}`, { duration: 15000 }); return; }
                  if (d.folder_error) {
                    toast.error(d.hint || `Sin acceso a carpeta. Conectado como ${d.connected_as}.`, { duration: 15000 });
                    return;
                  }
                  const ownerEmails = (d.folder_owners || []).map(o => o.emailAddress).join(', ');
                  const fileList = (d.files_in_folder || []).slice(0, 10).map(f => f.name).join(', ') || '(carpeta vacía o sin acceso de lectura)';
                  if (d.exact_match_found) {
                    toast.success(
                      `✅ OK · Conectado: ${d.connected_as} · Carpeta "${d.folder_name}" (${d.files_in_folder_count} archivos) · Archivo "${d.expected_filename}" encontrado.`,
                      { duration: 12000 }
                    );
                  } else if (d.files_in_folder_count === 0) {
                    toast.warning(
                      `⚠️ La carpeta "${d.folder_name}" existe (dueño: ${ownerEmails}) pero la cuenta conectada "${d.connected_as}" no ve ningún archivo. Posibles causas: (1) la cuenta sólo tiene permiso a la carpeta sin "ver archivos hijos", (2) los archivos están en subcarpetas, (3) GOOGLE_DRIVE_FOLDER_ID es de otra carpeta con el mismo nombre.`,
                      { duration: 25000 }
                    );
                  } else {
                    toast.warning(
                      `⚠️ Conectado: ${d.connected_as} · ${d.files_in_folder_count} archivos en carpeta pero ninguno con nombre exacto "${d.expected_filename}". Archivos: ${fileList}`,
                      { duration: 25000 }
                    );
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error');
                }
              }} size="sm" variant="outline" data-testid="btn-drive-diagnostic">
                Diagnóstico
              </Button>
              <Button onClick={triggerSync} size="sm" disabled={syncing} data-testid="btn-drive-sync">
                {syncing ? <Loader className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Sincronizar desde Drive
              </Button>
              <Button onClick={disconnect} size="sm" variant="ghost" data-testid="btn-drive-disconnect">
                <Unplug className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {files && (
        <div className="mt-3 border-t pt-3">
          <div className="text-xs text-muted-foreground mb-2">
            Archivos CSV en la carpeta ({files.length}):
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {files.map(f => (
              <div key={f.id} className="text-xs flex items-center justify-between px-2 py-1 bg-muted/40 rounded">
                <span className="truncate font-mono">{f.name}</span>
                <span className="text-muted-foreground ml-2 flex-shrink-0">
                  {f.size ? `${Math.round(f.size / 1024)} KB · ` : ''}{new Date(f.modifiedTime).toLocaleString('es-ES')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
